/**
 * Ödeme niyeti (Aşama 2 + 3 · tarayıcı ölse de satış tamamlansın).
 *
 * Her ödemede yazarkasaya/cihaza gitmeden ÖNCE sunucuya bir "ödeme niyeti" yazılır:
 * işlem anahtarı (IdempotencyKey), uç nokta ve satış kaydında POST edilecek isteğin
 * kendisi. Cihaz parayı aldıktan sonra son istek "cihaz sonucu" olarak bildirilir;
 * tarayıcı satışı kaydedemeden kapanırsa sunucudaki iş ~2 dk sonra satışı AYNI anahtarla
 * tamamlar (mükerrer kayıt olmaz). Cihaz parayı ALMADIYSA niyet terk edilir.
 *
 * Kurallar:
 * - Hiçbir fonksiyon hata FIRLATMAZ; kasiyere pencere göstermez (yalnızca telemetri).
 * - Niyet yazılamazsa ödeme normal devam eder (önce kasanın çalışması).
 * - Sunucuda niyet tablosu yoksa (Success:false / Status -1) ya da uç nokta yoksa
 *   (404/405) bir süre hiç denenmez → eski sunucuda ek bekleme olmaz.
 * - Payload.IdempotencyKey ile IntentKey AYNI olmak zorunda; farklıysa niyet yazılmaz
 *   (sunucu farklı anahtarla ikinci satış açmasın).
 */
import { salesApi } from '../config/apiClient';
import { classifyPosError, reportPosSaveError } from './posTelemetryService';

export type PaymentIntentEndpoint =
  | 'process-cash-with-invoice'
  | 'process-pos-with-invoice'
  | 'process-partial-with-invoice'
  | 'process-open-account-with-invoice'
  | 'process-other-payment-with-invoice'
  | 'process-online-payment-with-invoice';

export type PaymentIntentKind = 'nakit' | 'pos' | 'karma' | 'acik-hesap' | 'diger' | 'online';

/** pavo_rest_p2p | pavo_p2p | pavopay | beko | ingenico | inpos | hugin | yok */
export type PaymentIntentDevice = string;

export type PaymentIntentScreenMode = 'restoran' | 'hizli-satis' | 'paket';

export interface CreatePaymentIntentInput {
  /** = satış isteğinin IdempotencyKey'i */
  intentKey: string;
  /** Uç nokta adı; '/api/QuickSaleWithInvoice/' önekiyle de verilebilir */
  endpoint: PaymentIntentEndpoint | string;
  amount: number | null | undefined;
  paymentKind: PaymentIntentKind;
  fiscalDevice: PaymentIntentDevice;
  deviceCode?: string | null;
  /** Pavo Rest-P2P / Pavo sipariş modu: sunucu sonucu cihazdan bu kimlikle öğrenir */
  pavoRequestId?: string | null;
  screenId?: number | string | null;
  screenMode: PaymentIntentScreenMode;
  branchId?: string | number | null;
  tableId?: string | number | null;
  activeSaleId?: number | string | null;
  tableName?: string | null;
  /** Ad soyad — e-posta GÖNDERİLMEZ */
  cashierName?: string | null;
  /** Satış kaydında POST edilecek istek (mali alanlar boş olabilir) */
  payload: any;
}

export interface PaymentIntentResult {
  ok: boolean;
  /** Sunucunun döndürdüğü Status (varsa) */
  status?: number | string | null;
}

export interface DeviceResultExtra {
  fiscalReference?: string | null;
  /** ör. 'onaylandi', 'cihaz-yok', 'kasiyer-devam-etti' */
  fiscalResult?: string | null;
  paidAt?: string | null;
  /**
   * Cihazda ödeme türü değiştiyse (ör. Pavo sipariş modunda nakit/kart/karma cihazda
   * seçilir) son uç nokta ve tür. Sözleşmede zorunlu değil; sunucu yoksayabilir.
   */
  endpoint?: PaymentIntentEndpoint | string | null;
  paymentKind?: PaymentIntentKind | null;
}

const CREATE_TIMEOUT_MS = 4_000;
const DEVICE_RESULT_TIMEOUT_MS = 5_000;
const ABANDON_TIMEOUT_MS = 8_000;
/** Sunucu niyet desteklemiyorsa bu süre boyunca hiç denenmez */
const UNSUPPORTED_BACKOFF_MS = 10 * 60 * 1000;

/** Bu sayfada sunucuya başarıyla yazılmış niyetler (cihaz sonucu / terk yalnızca bunlar için gider) */
const openIntents = new Set<string>();
/** Bu sayfada yazılması DENENMİŞ niyetler (aynı anahtarla ikinci kez denenmez) */
const attemptedIntents = new Set<string>();
let unsupportedUntil = 0;

/** Ödemenin işlem anahtarı (IdempotencyKey = IntentKey). */
export const newPaymentIntentKey = (): string => {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  } catch { /* yoksay */ }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (ch) => {
    const r = (Math.random() * 16) | 0;
    const v = ch === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

/** Bu anahtar için sunucuda niyet açıldı mı (bu sayfada)? */
export const hasPaymentIntent = (intentKey: string | null | undefined): boolean =>
  !!intentKey && openIntents.has(intentKey);

/** Bu anahtar için niyet yazılması bu sayfada denendi mi (başarılı ya da değil)? */
export const wasPaymentIntentAttempted = (intentKey: string | null | undefined): boolean =>
  !!intentKey && attemptedIntents.has(intentKey);

const endpointName = (endpoint: string | null | undefined): string =>
  String(endpoint || '').replace(/^\/?api\/QuickSaleWithInvoice\//i, '').replace(/^\//, '');

const isUnsupportedStatus = (status: unknown): boolean => String(status ?? '').trim() === '-1';

const toNumberOrNull = (v: unknown): number | null => {
  if (v === null || v === undefined || v === '') return null;
  const n = typeof v === 'number' ? v : parseInt(String(v), 10);
  return Number.isFinite(n) ? n : null;
};

const toStringOrNull = (v: unknown): string | null => {
  if (v === null || v === undefined) return null;
  const s = String(v).trim();
  return s.length > 0 ? s : null;
};

/** E-posta asla gönderilmez */
const safeCashierName = (name: string | null | undefined): string | null => {
  const v = (name || '').trim();
  return v && !v.includes('@') ? v : null;
};

const errorDetails = (err: any) => ({
  message: String(err?.response?.data?.Message || err?.message || err || 'Bilinmeyen hata'),
  httpStatus: (err?.response?.status as number | undefined) ?? null,
});

/**
 * Cihaz çağrısından ÖNCE ödeme niyetini yazar (en fazla 4 sn).
 * Hiçbir zaman hata fırlatmaz; başarısızlıkta { ok:false } döner ve telemetri bildirir.
 */
export const createPaymentIntent = async (input: CreatePaymentIntentInput): Promise<PaymentIntentResult> => {
  const key = input?.intentKey;
  if (!key) return { ok: false };
  if (openIntents.has(key)) return { ok: true };
  if (attemptedIntents.has(key)) return { ok: false };
  attemptedIntents.add(key);

  if (Date.now() < unsupportedUntil) return { ok: false, status: -1 };

  const endpoint = endpointName(input.endpoint);
  const payloadKey = input.payload?.IdempotencyKey;
  if (!input.payload || payloadKey !== key) {
    console.warn('[PaymentIntent] Niyet yazılmadı: Payload.IdempotencyKey ≠ IntentKey', { key, payloadKey });
    return { ok: false };
  }

  const telemetryBase = {
    idempotencyKey: key,
    endpoint: `/api/PaymentIntents (${endpoint})`,
    branchId: toStringOrNull(input.branchId),
    screenId: toNumberOrNull(input.screenId),
    screenMode: input.screenMode,
    tableName: input.tableName ?? null,
    paymentKind: input.paymentKind,
    fiscalDevice: input.fiscalDevice,
    fiscalDone: false,
    pavoRequestId: input.pavoRequestId ?? null,
    amount: input.amount ?? null,
    cashierName: safeCashierName(input.cashierName),
    clientOutcome: 0 as const,
  };

  const startedAt = Date.now();
  try {
    const res = await salesApi.post<any>(
      '/api/PaymentIntents',
      {
        IntentKey: key,
        Endpoint: endpoint,
        Amount: Number(input.amount ?? 0) || 0,
        PaymentKind: input.paymentKind,
        FiscalDevice: input.fiscalDevice || 'yok',
        DeviceCode: input.deviceCode ?? null,
        PavoRequestId: input.pavoRequestId ?? null,
        ScreenId: toNumberOrNull(input.screenId),
        ScreenMode: input.screenMode,
        BranchId: toStringOrNull(input.branchId),
        TableId: toStringOrNull(input.tableId),
        ActiveSaleId: toNumberOrNull(input.activeSaleId),
        TableName: input.tableName ?? null,
        CashierName: safeCashierName(input.cashierName),
        Payload: input.payload,
      },
      { timeout: CREATE_TIMEOUT_MS }
    );
    const data = res?.data ?? {};
    const success = data.Success ?? data.success;
    const status = data.Status ?? data.status ?? null;
    if (success === true) {
      openIntents.add(key);
      return { ok: true, status };
    }
    if (isUnsupportedStatus(status)) {
      // Sunucuda niyet tablosu yok: hata değil, eski davranış
      unsupportedUntil = Date.now() + UNSUPPORTED_BACKOFF_MS;
      return { ok: false, status };
    }
    const message = String(data.Message || data.message || 'Sunucu niyeti kabul etmedi');
    reportPosSaveError({
      ...telemetryBase,
      errorMessage: `Ödeme niyeti kaydedilemedi: ${message}`,
      errorKind: 'sunucu',
      httpStatus: res?.status ?? null,
      durationMs: Date.now() - startedAt,
    });
    return { ok: false, status };
  } catch (err: any) {
    const { message, httpStatus } = errorDetails(err);
    if (httpStatus === 404 || httpStatus === 405) {
      // Uç nokta yok (eski sunucu): bir süre deneme
      unsupportedUntil = Date.now() + UNSUPPORTED_BACKOFF_MS;
      return { ok: false, status: -1 };
    }
    console.warn('[PaymentIntent] Niyet yazılamadı (ödeme devam ediyor):', message);
    reportPosSaveError({
      ...telemetryBase,
      errorMessage: `Ödeme niyeti kaydedilemedi: ${message}`,
      errorKind: classifyPosError(message, httpStatus),
      httpStatus,
      durationMs: Date.now() - startedAt,
    });
    return { ok: false };
  }
};

/**
 * Cihaz parayı aldı: son isteği (mali alanlarla) bildirir → sunucu tarayıcı kaydetmezse
 * satışı tamamlayacak işi planlar. Satış kaydı isteğinden ÖNCE başlatılmalıdır;
 * beklemek zorunlu değildir (en fazla 5 sn). Hiçbir zaman hata fırlatmaz.
 * Bu sayfada niyeti açılmamış anahtarlar için hiçbir şey göndermez.
 */
export const reportDeviceResult = async (
  intentKey: string | null | undefined,
  payload: any,
  extra: DeviceResultExtra = {}
): Promise<boolean> => {
  if (!intentKey || !openIntents.has(intentKey)) return false;
  if (!payload || payload.IdempotencyKey !== intentKey) {
    console.warn('[PaymentIntent] Cihaz sonucu gönderilmedi: Payload.IdempotencyKey ≠ IntentKey', intentKey);
    return false;
  }
  const startedAt = Date.now();
  try {
    const res = await salesApi.post<any>(
      `/api/PaymentIntents/${encodeURIComponent(intentKey)}/device-result`,
      {
        Payload: payload,
        FiscalReference: extra.fiscalReference ?? null,
        FiscalResult: extra.fiscalResult ?? null,
        PaidAt: extra.paidAt ?? new Date().toISOString(),
        ...(extra.endpoint ? { Endpoint: endpointName(extra.endpoint) } : {}),
        ...(extra.paymentKind ? { PaymentKind: extra.paymentKind } : {}),
      },
      { timeout: DEVICE_RESULT_TIMEOUT_MS }
    );
    const data = res?.data ?? {};
    const success = data.Success ?? data.success;
    if (success === false) {
      const message = String(data.Message || data.message || 'Sunucu cihaz sonucunu kabul etmedi');
      console.warn('[PaymentIntent] Cihaz sonucu kabul edilmedi:', message);
      reportPosSaveError({
        errorMessage: `Ödeme niyeti cihaz sonucu kaydedilemedi: ${message}`,
        errorKind: 'sunucu',
        httpStatus: res?.status ?? null,
        idempotencyKey: intentKey,
        endpoint: `/api/PaymentIntents/device-result`,
        fiscalDone: true,
        fiscalReference: extra.fiscalReference ?? null,
        durationMs: Date.now() - startedAt,
        clientOutcome: 0,
      });
      return false;
    }
    return true;
  } catch (err: any) {
    const { message, httpStatus } = errorDetails(err);
    console.warn('[PaymentIntent] Cihaz sonucu gönderilemedi (satış kaydı devam ediyor):', message);
    reportPosSaveError({
      errorMessage: `Ödeme niyeti cihaz sonucu kaydedilemedi: ${message}`,
      errorKind: classifyPosError(message, httpStatus),
      httpStatus,
      idempotencyKey: intentKey,
      endpoint: `/api/PaymentIntents/device-result`,
      fiscalDone: true,
      fiscalReference: extra.fiscalReference ?? null,
      durationMs: Date.now() - startedAt,
      clientOutcome: 0,
    });
    return false;
  }
};

/**
 * Para ALINMADI (cihaz reddetti / iptal / cihaza hiç ulaşılamadı): niyeti terk eder.
 * Sonuç belirsizse (zaman aşımı) ÇAĞRILMAMALIDIR. Fire-and-forget; hata fırlatmaz.
 */
export const abandonPaymentIntent = (intentKey: string | null | undefined, reason?: string | null): void => {
  if (!intentKey || !openIntents.has(intentKey)) return;
  openIntents.delete(intentKey);
  void salesApi
    .post(
      `/api/PaymentIntents/${encodeURIComponent(intentKey)}/abandon`,
      { Reason: reason ? String(reason).slice(0, 500) : null },
      { timeout: ABANDON_TIMEOUT_MS }
    )
    .catch((err: any) => {
      console.warn('[PaymentIntent] Niyet terk bildirimi gönderilemedi:', err?.message);
    });
};

/**
 * Cihaz hatası parayı KESİN olarak almamış mı? Zaman aşımı / bağlantı kopması /
 * "belirsiz" ifadeleri belirsiz sayılır (terk edilmez).
 */
export const isDeviceFailureCertain = (message: string | null | undefined): boolean => {
  const m = (message || '').toLocaleLowerCase('tr-TR');
  if (!m) return false;
  return !(
    m.includes('zaman aşımı') || m.includes('zaman asimi') || m.includes('timeout') ||
    m.includes('yanıt alınamadı') || m.includes('yanit alinamadi') || m.includes('belirsiz') ||
    m.includes('doğrulama') || m.includes('dogrulama') || m.includes('network') ||
    m.includes('failed to fetch') || m.includes('bağlantı koptu') || m.includes('aborted') ||
    m.includes('beklemede') || m.includes('devam ediyor') || m.includes('işleniyor') ||
    m.includes('yanıt vermedi') || m.includes('cevap') || /http 5\d\d/.test(m)
  );
};
